import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, useParams } from "react-router-dom";

import { Chart } from "../components/Chart";
import { FunnelFlow, FunnelSetupNote, FunnelStockPanel } from "../components/Funnel";
import { HealthPanel } from "../components/HealthPanel";
import { RangePicker } from "../components/RangePicker";
import {
  Card,
  Chip,
  DataGaps,
  Drawer,
  Empty,
  ErrorState,
  FreshnessBar,
  Headline,
  Level,
  Loading,
  StatusChip,
  Tile,
} from "../components/ui";
import { api } from "../lib/api";
import { downloadFile } from "../lib/download";
import { lineOption, mrrBridgeOption, palette } from "../lib/charts";
import {
  ago,
  count,
  date,
  dateShort,
  dateTime,
  LABELS,
  money,
  moneyExact,
  percent,
  ratio,
} from "../lib/format";
import type {
  Customer,
  EventsPage,
  Freshness,
  Funnel,
  Health,
  Integration,
  Movement,
  PeriodMetrics,
  ProductDetail,
  SeoQuery,
  SeriesPoint,
} from "../lib/types";

type Overview = {
  product: ProductDetail;
  current: PeriodMetrics;
  previous: PeriodMetrics;
  health: Health;
  freshness: Freshness[];
};

const TABS = [
  ["", "Przegląd"],
  ["klienci", "Klienci"],
  ["mrr", "Ruchy MRR"],
  ["zdarzenia", "Zdarzenia"],
  ["seo", "SEO"],
  ["integracje", "Integracje"],
] as const;

export function ProductPage() {
  const { slug = "", tab = "" } = useParams();
  const [range, setRange] = useState("30d");

  const overview = useQuery({
    queryKey: ["product", slug, "overview", range],
    queryFn: () => api.get<Overview>(`/products/${slug}/overview?range=${range}`),
  });

  if (overview.isPending) return <Loading />;
  if (overview.isError) return <ErrorState error={overview.error} />;
  if (!overview.data) return <Empty title="Nie ma takiego produktu" />;

  const { product, current, previous, health, freshness } = overview.data;
  const currency = product.default_currency;

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow" style={{ color: product.accent_color }}>
            Produkt
          </p>
          <h1>{product.name}</h1>
          <p className="page-head__sub">
            {product.tagline ?? "—"}
            {product.launched_at && ` · start ${date(product.launched_at)}`}
          </p>
          <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.6rem", flexWrap: "wrap" }}>
            <StatusChip value={product.status} dictionary={LABELS.productStatus} />
            {product.activation_event && (
              <Chip title="Zdarzenie, po którym uznajemy, że użytkownik zrozumiał wartość produktu">
                aktywacja: {product.activation_event}
              </Chip>
            )}
            {!product.capabilities.accounts && (
              <Chip tone="info" title="Produkt bez encji konta — konto mapowane 1:1 na użytkownika">
                model jednoosobowy
              </Chip>
            )}
            {product.tags.map((tag) => (
              <Chip key={tag} title="Etykieta własna — nie wpływa na żadną liczbę">
                {tag}
              </Chip>
            ))}
            <Link
              className="chip-link"
              to={`/koszty?produkt=${product.slug}`}
              title="Hosting, domena, prowizje i podwykonawcy tego produktu"
            >
              koszty
            </Link>
            <Link className="chip-link" to="/wdrozenie" title="Status, możliwości i etykiety produktu">
              zmień znaczniki
            </Link>
          </div>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <nav className="tabs" aria-label="Sekcje produktu">
        {TABS.map(([key, label]) => (
          <NavLink key={key} end to={`/produkt/${slug}${key ? `/${key}` : ""}`}>
            {label}
          </NavLink>
        ))}
      </nav>

      {tab === "" && (
        <OverviewTab
          slug={slug}
          range={range}
          currency={currency}
          current={current}
          previous={previous}
          health={health}
          freshness={freshness}
          accent={product.accent_color}
        />
      )}
      {tab === "klienci" && <CustomersTab slug={slug} currency={currency} />}
      {tab === "mrr" && <MovementsTab slug={slug} range={range} currency={currency} />}
      {tab === "zdarzenia" && <EventsTab slug={slug} />}
      {tab === "seo" && <SeoTab slug={slug} range={range} />}
      {tab === "integracje" && <IntegrationsTab slug={slug} />}
    </>
  );
}

/* ── Przegląd ─────────────────────────────────────────────────────────────── */

/**
 * Przegląd produktu ułożony w te same cztery poziomy co przegląd portfolio:
 * jedna liczba wiodąca → lejek → skąd wzięła się zmiana → co jest zepsute.
 * Reszta wskaźników siedzi w szufladzie. Dwa widoki w jednym narzędziu
 * czytające się inaczej to dwa narzędzia.
 */
function OverviewTab({
  slug,
  range,
  currency,
  current,
  previous,
  health,
  freshness,
  accent,
}: {
  slug: string;
  range: string;
  currency: string;
  current: PeriodMetrics;
  previous: PeriodMetrics;
  health: Health;
  freshness: Freshness[];
  accent: string;
}) {
  const series = useQuery({
    queryKey: ["product", slug, "series", range],
    queryFn: () =>
      api.get<{ series: SeriesPoint[] }>(
        `/products/${slug}/series?range=${range}&fields=mrr_reporting_minor,customers_active,trials_active,users_registered,users_activated,organic_sessions,paid_sessions`,
      ),
  });

  const funnel = useQuery({
    queryKey: ["product", slug, "funnel", range],
    queryFn: () => api.get<Funnel>(`/products/${slug}/funnel?range=${range}`),
  });

  const asMoney = (value: number) => money(Math.round(value), currency, { compact: true });
  const derived = current.derived;

  const charts = useMemo(() => {
    const rows = series.data?.series ?? [];
    if (!rows.length) return null;
    const p = palette();
    const days = rows.map((row) => dateShort(String(row.date)));
    const pick = (field: string) => rows.map((row) => Number(row[field] ?? 0));

    return {
      mrr: lineOption(
        days,
        [{ name: "MRR", values: pick("mrr_reporting_minor"), color: accent, area: true }],
        asMoney,
      ),
      audience: lineOption(
        days,
        [
          { name: "Klienci", values: pick("customers_active"), color: p.series[1]! },
          { name: "Triale", values: pick("trials_active"), color: p.series[2]! },
          { name: "Rejestracje", values: pick("users_registered"), color: p.series[4]! },
        ],
        (value) => count(Math.round(value)),
      ),
      traffic: lineOption(
        days,
        [
          { name: "Organiczny", values: pick("organic_sessions"), color: p.series[5]! },
          { name: "Płatny", values: pick("paid_sessions"), color: p.series[3]! },
        ],
        (value) => count(Math.round(value)),
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series.data, accent, currency]);

  const bridge = useMemo(
    () =>
      mrrBridgeOption(
        {
          start: current.mrr_start_minor,
          new: current.mrr_movements.new_minor,
          expansion: current.mrr_movements.expansion_minor,
          reactivation: current.mrr_movements.reactivation_minor,
          contraction: current.mrr_movements.contraction_minor,
          churn: current.mrr_movements.churned_minor,
          end: current.mrr_minor,
        },
        asMoney,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current, currency],
  );

  return (
    <div className="stack">
      <FreshnessBar items={freshness} />
      <DataGaps gaps={current.data_gaps} />

      <Headline
        label={`MRR · ${currency}`}
        value={money(current.mrr_minor, currency)}
        current={current.mrr_minor}
        previous={previous.mrr_minor}
        meta={
          <span title={`pewność wyniku ${percent(health.confidence, 0)}`}>
            health {health.score.toFixed(0)} ({health.grade})
          </span>
        }
        support={[
          {
            label: "Klienci płacący",
            value: count(current.customers),
            foot: `+${count(current.customers_new)} nowych w okresie`,
          },
          {
            label: "ARPA",
            value:
              derived.arpa_minor?.available && derived.arpa_minor.value !== null
                ? money(derived.arpa_minor.value, currency)
                : "—",
            foot: "na płacące konto",
            title: derived.arpa_minor?.reason ?? undefined,
          },
          {
            label: "Marża miesięczna",
            value: money(current.gross_margin_minor, currency, { compact: true }),
            foot: "po kosztach i reklamie",
          },
        ]}
      />

      <Level title="Lejek" question="Skąd biorą się klienci i na którym kroku ich tracimy?">
        {funnel.isPending ? (
          <Loading label="Składam lejek…" />
        ) : funnel.isError ? (
          <ErrorState error={funnel.error} />
        ) : funnel.data ? (
          <>
            <FunnelSetupNote setup={funnel.data.setup} />
            <div className="grid grid--2">
              <Card
                title="Lejek okresu"
                note="Długość paska to konwersja z poprzedniego etapu, nie udział w ruchu"
              >
                <FunnelFlow stages={funnel.data.flow} />
              </Card>
              <Card
                title="Stan bazy"
                note={`Ile kont żyje i na czym siedzą · stan na ${funnel.data.stock.as_of}`}
              >
                <FunnelStockPanel stock={funnel.data.stock} currency={currency} />
              </Card>
            </div>
          </>
        ) : null}
      </Level>

      <Level title="Przychód" question="Skąd wzięła się zmiana MRR?">
        <div className="grid grid--2">
          <Card title="MRR w czasie">
            <Chart option={charts?.mrr ?? null} ariaLabel="MRR produktu w czasie" />
          </Card>
          <Card title="Most MRR" note="Suma słupków domyka różnicę między początkiem a końcem okresu">
            <Chart option={bridge} ariaLabel="Most MRR produktu" />
          </Card>
        </div>
      </Level>

      <Level title="Kondycja" question="Co dokładnie obniża wynik produktu?">
        <Card
          title="Health score"
          note="Rozbicie na składowe — wynik bez rozbicia nie mówi, co naprawić"
        >
          <HealthPanel health={health} />
        </Card>
      </Level>

      <Level title="Szczegóły" question="Reszta wskaźników — potrzebna rzadziej niż raz dziennie">
        <Drawer summary="Wszystkie wskaźniki okresu">
          <div className="grid grid--tiles">
            <Tile
              label="ARR"
              value={money(current.arr_minor, currency, { compact: true })}
              hint="MRR × 12"
            />
            <Tile
              label="Churn klientów"
              value={
                derived.customer_churn?.available && derived.customer_churn.value !== null
                  ? percent(derived.customer_churn.value)
                  : "—"
              }
              muted={!derived.customer_churn?.available}
              hint={derived.customer_churn?.reason ?? undefined}
            />
            <Tile
              label="Konwersja triali"
              value={
                derived.trial_conversion?.available && derived.trial_conversion.value !== null
                  ? percent(derived.trial_conversion.value, 0)
                  : "—"
              }
              muted={!derived.trial_conversion?.available}
              hint={derived.trial_conversion?.reason ?? undefined}
            />
            <Tile
              label="Aktywacja"
              value={
                derived.activation_rate?.available && derived.activation_rate.value !== null
                  ? percent(derived.activation_rate.value, 0)
                  : "—"
              }
              muted={!derived.activation_rate?.available}
              hint={derived.activation_rate?.reason ?? undefined}
            />
            <Tile
              label="Retencja przychodu"
              value={
                derived.net_revenue_retention?.available &&
                derived.net_revenue_retention.value !== null
                  ? percent(derived.net_revenue_retention.value, 0)
                  : "—"
              }
              muted={!derived.net_revenue_retention?.available}
              hint={derived.net_revenue_retention?.reason ?? undefined}
            />
            <Tile
              label="CAC"
              value={
                derived.cac_minor?.available && derived.cac_minor.value !== null
                  ? money(derived.cac_minor.value, currency)
                  : "—"
              }
              muted={!derived.cac_minor?.available}
              hint={derived.cac_minor?.reason ?? undefined}
            />
            <Tile
              label="LTV / CAC"
              value={
                derived.ltv_cac?.available && derived.ltv_cac.value !== null
                  ? ratio(derived.ltv_cac.value)
                  : "—"
              }
              muted={!derived.ltv_cac?.available}
              hint={derived.ltv_cac?.reason ?? undefined}
            />
            <Tile
              label="Nieudane płatności"
              value={
                derived.payment_failure_rate?.available &&
                derived.payment_failure_rate.value !== null
                  ? percent(derived.payment_failure_rate.value, 1)
                  : "—"
              }
              muted={!derived.payment_failure_rate?.available}
              hint={derived.payment_failure_rate?.reason ?? undefined}
            />
          </div>

          <div className="grid grid--2" style={{ marginTop: "var(--space-5)" }}>
            <Card title="Klienci, triale i rejestracje">
              <Chart
                option={charts?.audience ?? null}
                ariaLabel="Klienci, triale i rejestracje w czasie"
              />
            </Card>
            <Card title="Ruch" note="Sesje dziennie">
              <Chart option={charts?.traffic ?? null} ariaLabel="Ruch organiczny i płatny produktu" />
            </Card>
          </div>
        </Drawer>
      </Level>
    </div>
  );
}

/* ── Klienci ──────────────────────────────────────────────────────────────── */

function CustomersTab({ slug, currency }: { slug: string; currency: string }) {
  const [status, setStatus] = useState("");
  const customers = useQuery({
    queryKey: ["product", slug, "customers", status],
    queryFn: () =>
      api.get<{ customers: Customer[] }>(
        `/products/${slug}/customers?limit=200${status ? `&status=${status}` : ""}`,
      ),
  });

  return (
    <Card
      title="Klienci"
      note="Dane analityczne, nie operacyjne — bez adresów, telefonów i dokumentów"
      flush
      action={
        <select
          className="btn"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label="Filtruj po statusie"
        >
          <option value="">wszyscy</option>
          {Object.entries(LABELS.customerStatus).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      }
    >
      {customers.isPending ? (
        <Loading />
      ) : customers.data?.customers.length ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Klient</th>
                <th>Status</th>
                <th>Plan</th>
                <th className="num">MRR</th>
                <th>Rejestracja</th>
                <th>Trial</th>
                <th>Subskrypcja</th>
                <th>Ostatnia aktywność</th>
                <th>Pierwsze źródło</th>
                <th>Pierwsza kampania</th>
              </tr>
            </thead>
            <tbody>
              {customers.data.customers.map((customer) => (
                <tr key={customer.id}>
                  <td>
                    {customer.name ?? <span className="dim">bez nazwy</span>}
                    <div className="dim mono" style={{ fontSize: "0.72rem" }}>
                      {customer.external_account_id}
                    </div>
                  </td>
                  <td>
                    <StatusChip value={customer.status} dictionary={LABELS.customerStatus} />
                  </td>
                  <td>{customer.plan ?? <span className="dim">—</span>}</td>
                  <td className="num">
                    {customer.mrr_minor ? moneyExact(customer.mrr_minor, customer.currency ?? currency) : <span className="dim">—</span>}
                  </td>
                  <td>{date(customer.registered_at)}</td>
                  <td>{date(customer.trial_started_at)}</td>
                  <td>{date(customer.first_subscription_at)}</td>
                  <td>{date(customer.last_activity_at)}</td>
                  <td>{customer.first_source ?? <span className="dim">—</span>}</td>
                  <td>{customer.first_campaign ?? <span className="dim">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty title="Brak klientów w tym filtrze" />
      )}
    </Card>
  );
}

/* ── Ruchy MRR ────────────────────────────────────────────────────────────── */

const MOVEMENT_TONE: Record<string, "good" | "warn" | "crit" | "info" | "quiet"> = {
  new: "good",
  expansion: "good",
  reactivation: "info",
  contraction: "warn",
  churn: "crit",
};

function MovementsTab({ slug, range, currency }: { slug: string; range: string; currency: string }) {
  const movements = useQuery({
    queryKey: ["product", slug, "movements", range],
    queryFn: () => api.get<{ movements: Movement[] }>(`/products/${slug}/movements?range=${range}`),
  });

  return (
    <Card
      title="Rejestr ruchów MRR"
      note="Każda zmiana przychodu z nazwiskiem klienta i zdarzeniem źródłowym"
      flush
    >
      {movements.isPending ? (
        <Loading />
      ) : movements.data?.movements.length ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Data</th>
                <th>Rodzaj</th>
                <th>Klient</th>
                <th className="num">Zmiana</th>
                <th>Uwaga</th>
                <th>Zdarzenie źródłowe</th>
              </tr>
            </thead>
            <tbody>
              {movements.data.movements.map((movement, index) => (
                <tr key={`${movement.date}-${movement.customer.id}-${index}`}>
                  <td className="mono">{movement.date}</td>
                  <td>
                    <Chip tone={MOVEMENT_TONE[movement.kind] ?? "quiet"}>
                      {LABELS.movement[movement.kind] ?? movement.kind}
                    </Chip>
                  </td>
                  <td>{movement.customer.name ?? movement.customer.external_account_id}</td>
                  <td
                    className="num"
                    style={{ color: movement.delta_minor < 0 ? "var(--crit)" : "var(--good)" }}
                  >
                    {movement.delta_minor > 0 ? "+" : "−"}
                    {moneyExact(Math.abs(movement.delta_minor), movement.currency || currency)}
                  </td>
                  <td className="dim">{movement.note ?? "—"}</td>
                  <td className="mono dim" style={{ fontSize: "0.72rem" }}>
                    {movement.source_event_id ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty title="Brak ruchów MRR w tym zakresie" />
      )}
    </Card>
  );
}

/* ── Zdarzenia ────────────────────────────────────────────────────────────── */

const EVENTS_PAGE_SIZE = 50;

/**
 * Pełna historia zdarzeń, po 50 na stronę. Wcześniej widok brał 100 ostatnich
 * rekordów, co przy kilkudziesięciu zdarzeniach dziennie kończyło historię
 * na trzech dniach — i nic na ekranie nie mówiło, że dalej coś jeszcze jest.
 */
function EventsTab({ slug }: { slug: string }) {
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [eventName, setEventName] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState<{ busy: boolean; error?: string }>({ busy: false });

  const filter = `${onlyErrors ? "&only_errors=1" : ""}${
    eventName ? `&event_name=${encodeURIComponent(eventName)}` : ""
  }`;
  const events = useQuery({
    queryKey: ["product", slug, "events", onlyErrors, eventName, page],
    queryFn: () =>
      api.get<EventsPage>(
        `/products/${slug}/events?page=${page}&page_size=${EVENTS_PAGE_SIZE}${filter}`,
      ),
    placeholderData: (previous) => previous,
  });

  const pages = events.data ? Math.max(1, Math.ceil(events.data.total / events.data.page_size)) : 1;
  const exportCsv = async () => {
    setExporting({ busy: true });
    try {
      await downloadFile(`/export/events?products=${slug}${filter}`);
      setExporting({ busy: false });
    } catch (error) {
      setExporting({ busy: false, error: error instanceof Error ? error.message : "Błąd eksportu" });
    }
  };

  return (
    <Card
      title="Strumień zdarzeń"
      note={
        events.data
          ? `${count(events.data.total)} zdarzeń · nierozwiązane w kolejce błędów: ${events.data.dead_letters_open}`
          : undefined
      }
      flush
      action={
        <div className="events-actions">
          <select
            aria-label="Nazwa zdarzenia"
            value={eventName}
            onChange={(event) => {
              setEventName(event.target.value);
              setPage(1);
            }}
          >
            <option value="">wszystkie zdarzenia</option>
            {events.data?.event_names.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn"
            aria-pressed={onlyErrors}
            onClick={() => {
              setOnlyErrors((value) => !value);
              setPage(1);
            }}
          >
            {onlyErrors ? "pokaż wszystkie" : "tylko błędy"}
          </button>
          <button type="button" className="btn" disabled={exporting.busy} onClick={exportCsv}>
            {exporting.busy ? "Eksportuję…" : "Eksport CSV"}
          </button>
        </div>
      }
    >
      {exporting.error && <p className="form-error">{exporting.error}</p>}
      {events.isPending ? (
        <Loading />
      ) : events.isError ? (
        <ErrorState error={events.error} />
      ) : events.data?.events.length ? (
        <>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Wystąpiło</th>
                  <th>Dotarło</th>
                  <th>Zdarzenie</th>
                  <th>Konto</th>
                  <th>Użytkownik</th>
                  <th>Środowisko</th>
                  <th>Stan</th>
                </tr>
              </thead>
              <tbody>
                {events.data.events.map((event) => {
                  const late =
                    new Date(event.received_at).getTime() - new Date(event.occurred_at).getTime() >
                    86_400_000;
                  return (
                    <tr key={event.id}>
                      <td className="mono">{dateTime(event.occurred_at)}</td>
                      <td className="mono">
                        {dateTime(event.received_at)}
                        {late && (
                          <Chip tone="warn" title="Zdarzenie dotarło ponad dobę po wystąpieniu">
                            spóźnione
                          </Chip>
                        )}
                      </td>
                      <td className="mono">{event.event_name}</td>
                      <td className="mono dim">{event.external_account_id ?? "—"}</td>
                      <td className="mono dim">{event.external_user_id ?? "—"}</td>
                      <td>{event.environment}</td>
                      <td>
                        {event.process_error ? (
                          <Chip tone="crit" title={event.process_error}>
                            błąd
                          </Chip>
                        ) : event.processed_at ? (
                          <Chip tone="good">przetworzone</Chip>
                        ) : (
                          <Chip tone="warn">w kolejce</Chip>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <nav className="pager" aria-label="Strony historii zdarzeń">
            <button type="button" className="btn btn--quiet" disabled={page <= 1} onClick={() => setPage(1)}>
              najnowsze
            </button>
            <button
              type="button"
              className="btn btn--quiet"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              ← nowsze
            </button>
            <span className="pager__where mono" aria-live="polite">
              strona {page} z {pages}
            </span>
            <button
              type="button"
              className="btn btn--quiet"
              disabled={page >= pages}
              onClick={() => setPage((value) => value + 1)}
            >
              starsze →
            </button>
            <button
              type="button"
              className="btn btn--quiet"
              disabled={page >= pages}
              onClick={() => setPage(pages)}
            >
              najstarsze
            </button>
          </nav>
        </>
      ) : (
        <Empty
          title={eventName || onlyErrors ? "Brak zdarzeń dla tego filtra" : "Brak zdarzeń"}
          hint={eventName || onlyErrors ? "Zmień filtr powyżej." : "Sprawdź, czy CRM ma poprawny klucz API."}
        />
      )}
    </Card>
  );
}

/* ── SEO ──────────────────────────────────────────────────────────────────── */

function SeoTab({ slug, range }: { slug: string; range: string }) {
  const seo = useQuery({
    queryKey: ["product", slug, "seo", range],
    queryFn: () => api.get<{ queries: SeoQuery[] }>(`/products/${slug}/seo?range=${range}`),
  });

  return (
    <Card
      title="Frazy z Search Console"
      note="Pozycja ważona wyświetleniami — zwykła średnia ze średnich nie opisuje niczego rzeczywistego"
      flush
    >
      {seo.isPending ? (
        <Loading />
      ) : seo.data?.queries.length ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Fraza</th>
                <th className="num">Kliknięcia</th>
                <th className="num">Wyświetlenia</th>
                <th className="num">CTR</th>
                <th className="num">Pozycja</th>
                <th>Okazja</th>
              </tr>
            </thead>
            <tbody>
              {seo.data.queries.map((row) => (
                <tr key={row.query}>
                  <td>{row.query}</td>
                  <td className="num">{count(row.clicks)}</td>
                  <td className="num">{count(row.impressions)}</td>
                  <td className="num">{percent(row.ctr, 1)}</td>
                  <td className="num">{row.position.toFixed(1)}</td>
                  <td>
                    {row.quick_win ? (
                      <Chip tone="warn" title="Jest widoczność, brakuje pozycji w pierwszej trójce">
                        quick win
                      </Chip>
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty title="Brak danych z Search Console" hint="Integracja GSC nie została jeszcze podłączona." />
      )}
    </Card>
  );
}

/* ── Integracje ───────────────────────────────────────────────────────────── */

function IntegrationsTab({ slug }: { slug: string }) {
  const integrations = useQuery({
    queryKey: ["product", slug, "integrations"],
    queryFn: () => api.get<{ integrations: Integration[] }>(`/products/${slug}/integrations`),
  });

  return (
    <Card
      title="Źródła danych"
      note="Termin świeżości jest częścią definicji źródła — po jego przekroczeniu dashboard mówi „nie wiem”"
      flush
    >
      {integrations.isPending ? (
        <Loading />
      ) : integrations.data?.integrations.length ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Źródło</th>
                <th>Stan</th>
                <th>Ostatni sukces</th>
                <th className="num">SLA</th>
                <th className="num">Wierszy</th>
                <th>Błąd</th>
              </tr>
            </thead>
            <tbody>
              {integrations.data.integrations.map((integration) => {
                const ageHours = integration.last_success_at
                  ? (Date.now() - new Date(integration.last_success_at).getTime()) / 3_600_000
                  : Infinity;
                const stale = ageHours > integration.freshness_sla_hours;
                return (
                  <tr key={integration.source}>
                    <td>{LABELS.source[integration.source] ?? integration.source}</td>
                    <td>
                      {!integration.is_enabled ? (
                        <Chip>wyłączona</Chip>
                      ) : stale ? (
                        <Chip tone="crit">poza SLA</Chip>
                      ) : (
                        <Chip tone="good">świeża</Chip>
                      )}
                    </td>
                    <td>{ago(integration.last_success_at)}</td>
                    <td className="num">{integration.freshness_sla_hours} h</td>
                    <td className="num">{integration.last_run?.rows_written ?? "—"}</td>
                    <td className="dim">{integration.last_error ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty title="Brak skonfigurowanych integracji" />
      )}
    </Card>
  );
}
